import { Injectable } from '@angular/core';
import { PermissionsService } from '@app/modules/auth';
import { DivisionMappingService } from '@app/services/division-mapping.service';
import { AutocompleteParams, AutocompleteResult } from '@app/modules/autocomplete-client/autocomplete-api.models';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, switchMap, map, combineLatestWith } from 'rxjs/operators';
import { AssetsParams } from '@app/modules/location-client/location-api.models';
import { deduplicateArray, extractIds, intersection } from '@app/modules/shared/utilities/utilities';
import { Store } from '@ngrx/store';
import { selectCurrentFilter } from '@app/store/filters/selectors/filters.selectors';
import { LocationFacade } from '@app/modules/location/facade/location.facade';

@Injectable({
  providedIn: 'root'
})
export class AutocompleteSearchService {
  constructor(
    private locationFacade: LocationFacade,
    private permissions: PermissionsService,
    private divisionMapper: DivisionMappingService, // For external users to fetch divisions
    private store: Store
  ) {}

  executeAutocompleteSearch(
    searchParams: Partial<AutocompleteParams>,
    assetParams: AssetsParams
  ): Observable<AutocompleteResult> {
    return this.permissions.isZonarUser().pipe(
      switchMap(isZonarUser => {
        return this.fetchAutocompleteForInternalUser(searchParams, assetParams);
      }),
      switchMap(results => {
        return this.createResultsFromApiRequests(results);
      }),
      catchError((error, _) => {
        return EMPTY;
      })
    );
  }

  /**
   * Returns the set of API results | autocomplete response based on parameters for Zonar users
   * @param searchParams
   * @param assetParams
   */
  fetchAutocompleteForInternalUser(
    searchParams: Partial<AutocompleteParams>,
    assetParams: AssetsParams
  ): Observable<any[]> {
    if (assetParams.divisionIds?.length) {
      // If we specify division IDs just fetch the assets for the given ids, as these are locations
      return this.fetchAutocompleteDivisionIds(searchParams, assetParams.divisionIds, assetParams.companyId);
    } else {
      // Or failing any parameters we will grab all the things based on the params.
      return this.fetchAutocompleteCompanyId(searchParams, assetParams.companyId).pipe(map(result => [result]));
    }
  }

  fetchAutocompleteDivisionIds(searchParams, divisionIds, companyId): Observable<AutocompleteResult[]> {
    const params: AutocompleteParams = { ...searchParams, divisionIds, companyId };
    return this.fetchAutocomplete(params).pipe(map(results => [results]));
  }

  fetchAutocompleteCompanyId(searchParams, companyId): Observable<AutocompleteResult> {
    const params: AutocompleteParams = { ...searchParams, companyId };
    return this.fetchAutocomplete(params);
  }

  fetchAutocomplete(params: AutocompleteParams): Observable<AutocompleteResult> {
    let currentFilter;
    this.store.select(selectCurrentFilter).subscribe(filter => {
      currentFilter = filter;
    });

    if (currentFilter.locations?.length && params.divisionIds?.length) {
      const divisions = currentFilter.divisions?.map(d => d.id);
      params.divisionIds = deduplicateArray([...params.divisionIds, ...divisions]);
    }
    return combineLatest([this.locationFacade.getAllAssets(), this.locationFacade.getAllDrivers()]).pipe(
      map(([assets, driversMap]) => {
        const filteredAssets: any[] = [];
        const filteredDrivers: any[] = [];
        this.locationFacade.filterAssets(assets, driversMap, currentFilter).forEach(asset => {
          const searchTerm = params.searchTerm?.toLowerCase() || '';
          const assetNameMatch =
            searchTerm &&
            (asset?.assetName?.toLowerCase().includes(searchTerm) ||
              asset?.assetDisplayName?.toLowerCase().includes(searchTerm));

          let driverNameMatch = false;
          if (asset.driverProfileId) {
            const driver = driversMap.get(asset.driverProfileId);
            if (driver) {
              const fullName = `${driver.legacyFirstName || ''} ${driver.legacyLastName || ''}`.toLowerCase();
              driverNameMatch = searchTerm && fullName.includes(searchTerm);
              if (driverNameMatch) {
                filteredDrivers.push({
                  ...asset,
                  driverFirstName: driver.legacyFirstName,
                  driverLastName: driver.legacyLastName
                });
              }
            }
          }
          if (assetNameMatch) {
            filteredAssets.push(asset);
          }
        });
        return {
          assets: filteredAssets,
          drivers: filteredDrivers
        };
      })
    );
    // return this.autocompleteApi.getAutocomplete({ ...params, pageSize: env.autocompleteApi.pageSize });
  }

  createResultsFromApiRequests(
    results: Array<AutocompleteResult> | Array<Array<AutocompleteResult>>
  ): Observable<AutocompleteResult> {
    if (!Array.isArray(results[0])) {
      return of(results[0]);
    }
    const temp = results[0].reduce(
      (acc, val) => {
        acc.drivers.push(...val.drivers);
        acc.assets.push(...val.assets);
        return acc;
      },
      { assets: [], drivers: [] }
    );
    return of({ ...temp, nextPageToken: '' });
  }

  /**
   *
   * @param paramDivisions
   * @param accountDivisions
   * @return list of divisions to fetch in paginated division flow.
   */
  getDivisionsToFetch(paramDivisions?: string[], accountDivisions?: string[]): string[] {
    // This is fun!! first if we have account divisions(account code selected
    let returnValue;
    if (paramDivisions?.length && accountDivisions?.length) {
      const availableAccounts = accountDivisions;
      // const childDivisionIds = extractIds(this.divisionMapper.getChildDivisionsForParentIds(accountDivisions));
      // This is essentially a check against whether the user is location locked. the first case is the false case.
      if (availableAccounts.length) {
        returnValue = [...availableAccounts, ...paramDivisions];
      } else {
        // User is location locked, only fetch for children
        returnValue = [...paramDivisions];
      }
    } else if (paramDivisions?.length) {
      // if just specified divisions, get all specified divisions and their children
      // const paramChildren = extractIds(this.divisionMapper.getChildDivisionsForParentIds(paramDivisions));
      returnValue = [...paramDivisions];
    } else if (accountDivisions?.length) {
      // If we only specify an account to filter, get all authorized accounts and their children
      // const children = extractIds(this.divisionMapper.getChildDivisionsForParentIds(accountDivisions));
      const allowedAccounts = extractIds(this.divisionMapper.getDivisionsForIds(accountDivisions)); // if loc locked, this will be empty
      returnValue = [...allowedAccounts];
    } else {
      // this actually should never happen, but logically TS doesn't know this.
      returnValue = [];
    }
    return returnValue;
  }
}
